import {type CountryCode, getExampleNumber, parsePhoneNumber} from "libphonenumber-js/mobile";
import examples from "libphonenumber-js/mobile/examples";
import {proxy, ref} from "valtio";

import {EventEmitter} from "@/shared/event-emitter";
import {IValtioModel} from "@/shared/models/base-model.interface";
import {BaseField, BaseFieldConfig} from "@/shared/models/form/fields";
import {CursorPosition, ValidationError} from "@/shared/models/form/fields/types";
import type {PhoneCountryData} from "@/shared/models/form/phone/phone-data";

export interface PhoneData {
    code: string; // ISO alpha2 code (e.g., 'US', 'UA', 'GB')
    name: string; // Country name for display
    phone_code: string; // International dialing code (e.g., '1', '380', '44')
}

interface PhoneFieldConfig extends BaseFieldConfig {
    initialCountry?: string; // Default country code (e.g., 'US', 'GB')
}

export interface PhoneFieldEvents {
    change: (value: string, country: string) => void;
    countryChange: (country: string) => void;
    valid: (isValid: boolean) => void;
    loading: (isLoading: boolean) => void;
    error: (error: ValidationError) => void;
    scrollIntoView: () => void;
}

class PhoneFieldClass extends BaseField implements IValtioModel {
    // Reactive state
    activeCountry: string;
    search: string;
    value: string;
    dropdownOpen: boolean;
    _allPhones: Array<PhoneData>;
    _filteredPhones: Array<PhoneData>;
    _isLoading: boolean;
    _cursorPosition: CursorPosition | null = null;

    // Non-reactive
    public readonly phoneEvents: EventEmitter<PhoneFieldEvents>;

    constructor(config: PhoneFieldConfig = {}) {
        super({
            disabled: config.disabled,
            validators: config.validators,
        });

        this.activeCountry = config.initialCountry ?? "US";
        this.search = "";
        this.value = "";
        this.dropdownOpen = false;
        this._allPhones = [];
        this._filteredPhones = [];
        this._isLoading = false;

        this.phoneEvents = ref(new EventEmitter<PhoneFieldEvents>()) as EventEmitter<PhoneFieldEvents>;
    }

    // Computed getters
    get allPhones(): Array<PhoneData> {
        return this._allPhones;
    }

    get filteredPhones(): Array<PhoneData> {
        return this._filteredPhones;
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    // Inherited from BaseField: errors, error, hasError, shouldShowError, isValid

    get selectedPhone(): PhoneData | undefined {
        return this._allPhones.find((item) => item.code === this.activeCountry);
    }

    get phoneCode(): string {
        return this.selectedPhone?.phone_code ?? "1"; // Fallback to USA (+1) while loading
    }

    /**
     * Get phone mask with # placeholders for the selected country
     * Generated dynamically from libphonenumber-js examples
     */
    get maskActiveSelected(): string {
        if (!this.selectedPhone) {
            // Fallback to USA mask while loading
            return "(###) ###-####";
        }

        try {
            const example = getExampleNumber(this.selectedPhone.code as CountryCode, examples);
            if (example) {
                const formatted = example.formatNational();
                // Replace all digits with #
                return formatted.replace(/\d/g, "#");
            }
        } catch (error) {
            console.log(error);
            // If no example available, return USA mask
        }

        return "(###) ###-####";
    }

    /**
     * Get placeholder text for input (example phone number)
     * Generated dynamically from libphonenumber-js examples
     */
    get inputPlaceholder(): string {
        if (!this.selectedPhone) {
            // Fallback to USA example while loading
            return "(201) 555-0123";
        }

        try {
            const example = getExampleNumber(this.selectedPhone.code as CountryCode, examples);
            if (example) {
                return example.formatNational();
            }
        } catch (error) {
            console.log(error);
            // If no example available, return USA example
        }

        return "(201) 555-0123";
    }

    /**
     * Get formatted phone number in international format
     * Returns null if number is invalid
     */
    get formattedInternational(): string | null {
        if (!this.value || !this.selectedPhone) return null;

        try {
            const cleanValue = this.value.replace(/\s/g, "");
            const fullNumber = `+${this.selectedPhone.phone_code}${cleanValue}`;
            const phoneNumber = parsePhoneNumber(fullNumber, this.selectedPhone.code as CountryCode);

            if (phoneNumber.isValid()) {
                return phoneNumber.formatInternational();
            }
        } catch (error) {
            console.log(error);
            // Invalid number
        }

        return null;
    }

    /**
     * Get formatted phone number in national format
     * Returns null if number is invalid
     */
    get formattedNational(): string | null {
        if (!this.value || !this.selectedPhone) return null;

        try {
            const cleanValue = this.value.replace(/\s/g, "");
            const fullNumber = `+${this.selectedPhone.phone_code}${cleanValue}`;
            const phoneNumber = parsePhoneNumber(fullNumber, this.selectedPhone.code as CountryCode);

            if (phoneNumber.isValid()) {
                return phoneNumber.formatNational();
            }
        } catch (error) {
            console.log(error);
            // Invalid number
        }

        return null;
    }

    validate(): {isValid: boolean; error?: string} {
        const errors: Array<ValidationError> = [];

        // Check if required and empty (using BaseField's isRequired getter)
        if (this.isRequired && !this.value) {
            errors.push({
                type: "error",
                message: this.requireMessage,
            });
        }

        // If no value and not required, it's valid
        if (!this.value && !this.isRequired) {
            this._errors = [];
            this.phoneEvents.emit("valid", true);
            return {isValid: true};
        }

        // Validate phone format if value exists
        if (this.value) {
            const selectedPhone = this.selectedPhone;
            if (!selectedPhone) {
                errors.push({
                    type: "error",
                    message: "invalidCountry",
                });
            } else {
                // Use libphonenumber-js for validation
                try {
                    const cleanValue = this.value.replace(/\s/g, "");
                    const fullNumber = `+${selectedPhone.phone_code}${cleanValue}`;

                    // Parse and validate
                    const phoneNumber = parsePhoneNumber(fullNumber, selectedPhone.code as CountryCode);

                    if (!phoneNumber.isValid()) {
                        errors.push({
                            type: "error",
                            message: "invalidPhone",
                        });
                    }
                } catch (error) {
                    console.log(error);
                    // If parsing fails, number is invalid
                    errors.push({
                        type: "error",
                        message: "invalidPhoneFormat",
                    });
                }
            }

            // Run custom validators from BaseField
            if (errors.length === 0) {
                const validationResult = this.runValidators(this.fullPhoneNumber);
                if (!validationResult.isValid) {
                    errors.push({
                        type: "error",
                        message: validationResult.error || "validationError",
                    });
                }
            }
        }

        this._errors = errors;
        const isValid = errors.length === 0;
        this.phoneEvents.emit("valid", isValid);

        if (errors.length > 0) {
            this.phoneEvents.emit("error", errors[0]);
        }

        return {
            isValid,
            error: errors[0]?.message,
        };
    }

    get formattedValue(): string {
        let formatted = "";
        let digitIndex = 0;
        const mask = this.maskActiveSelected;

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < mask.length; i++) {
            if (digitIndex >= this.value.length) break;

            const maskChar = mask[i];
            if (maskChar === "#") {
                formatted += this.value[digitIndex];
                digitIndex++;
            } else {
                formatted += maskChar;
            }
        }

        return formatted;
    }

    get fullPhoneNumber(): string {
        return `+${this.phoneCode}${this.value}`;
    }

    // Cursor position management
    get cursorPosition(): CursorPosition | null {
        return this._cursorPosition;
    }

    setCursorPosition(start: number, end: number): void {
        this._cursorPosition = {start, end};
    }

    clearCursorPosition(): void {
        this._cursorPosition = null;
    }

    /**
     * Adjusts cursor position from formatted value to new formatted value.
     * Counts digits before cursor in input, then finds position after same number of digits in output.
     */
    private adjustCursorForFormat(cursor: CursorPosition, inputValue: string): CursorPosition {
        // Count digits before cursor in the input (formatted) value
        const textBeforeCursor = inputValue.slice(0, cursor.start);
        const digitsBeforeCursor = (textBeforeCursor.match(/\d/g) || []).length;

        // Find position after same number of digits in the new formatted value
        const formatted = this.formattedValue;
        let digitCount = 0;
        let newPos = 0;

        for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i])) {
                digitCount++;
            }
            if (digitCount >= digitsBeforeCursor) {
                newPos = i + 1;
                break;
            }
            newPos = i + 1;
        }

        // If we ran out of formatted string, position at the end
        if (digitsBeforeCursor > digitCount) {
            newPos = formatted.length;
        }

        return {start: newPos, end: newPos};
    }

    searchFilter(value: string): void {
        this.search = value;
        this._filteredPhones = this._allPhones.filter(
            (item) => item.name.toLowerCase().includes(value.toLowerCase()) || item.code.toLowerCase().includes(value.toLowerCase()),
        );
    }

    changeCountry(countryCode: string): void {
        this.activeCountry = countryCode;
        this.value = "";
        this._errors = [];
        this.dropdownOpen = false;
        this.search = "";
        this._filteredPhones = this._allPhones;
        this.phoneEvents.emit("countryChange", countryCode);
        this.phoneEvents.emit("change", "", countryCode);
    }

    onChangePhone(val: string): void {
        // Save cursor position from input before processing
        const savedCursor = this._cursorPosition;

        // Clean input - allow only + and digits
        let cleanedInput = val.replace(/[^+0-9]/g, "");

        // Ensure + can only be at the beginning
        if (cleanedInput.indexOf("+") > 0) {
            cleanedInput = cleanedInput.replace(/\+/g, "");
        }
        // Remove duplicate + at the beginning
        if (cleanedInput.startsWith("+")) {
            cleanedInput = "+" + cleanedInput.substring(1).replace(/\+/g, "");
        }

        // Try to parse international number (e.g., from autocomplete or paste)
        if (cleanedInput.startsWith("+") && cleanedInput.length >= 5) {
            const parsed = this.tryParseInternationalNumber(cleanedInput);
            if (parsed) {
                // Successfully parsed - set country and national number
                this.activeCountry = parsed.country;
                this.value = parsed.nationalNumber;
                this.dropdownOpen = false;
                this.search = "";
                this._filteredPhones = this._allPhones;
                this._errors = [];

                // Adjust cursor to end of national number
                this._cursorPosition = {
                    start: this.formattedValue.length,
                    end: this.formattedValue.length,
                };

                this.phoneEvents.emit("countryChange", parsed.country);
                this.phoneEvents.emit("change", this.value, this.activeCountry);
                return;
            }
        }

        // If user types + but not enough for international parsing, show dropdown
        if (cleanedInput.startsWith("+")) {
            const codeSearch = cleanedInput.substring(1); // Remove + for search
            this.searchFilterByCode(codeSearch);
            if (!this.dropdownOpen) {
                this.dropdownOpen = true;
            }
            // Don't store + in value - prefix already shows it
            // Keep current value, just update dropdown filter
            this.phoneEvents.emit("change", this.value, this.activeCountry);
            return;
        }

        // Regular input (digits only, no +)
        let value = cleanedInput;

        // Limit value to mask length
        const mask = this.maskActiveSelected;
        if (mask) {
            // Count how many digits the mask allows (count # symbols)
            const maxDigits = (mask.match(/#/g) || []).length;

            // If value exceeds mask length, replace last digit with new one
            if (value.length > maxDigits) {
                // Take first (maxDigits - 1) digits + last digit from new input
                value = value.slice(0, maxDigits - 1) + value[value.length - 1];
            }
        }

        // Set value
        this.value = value;

        // Adjust cursor position for new formatted value
        if (savedCursor) {
            this._cursorPosition = this.adjustCursorForFormat(savedCursor, val);
        }

        // Clear errors when user types
        this._errors = [];

        // Close dropdown if it was open (+ was removed)
        if (this.dropdownOpen) {
            this.dropdownOpen = false;
            this.search = "";
            this._filteredPhones = this._allPhones;
        }

        this.phoneEvents.emit("change", this.value, this.activeCountry);
    }

    /**
     * Try to parse an international phone number (e.g., "+380501234567")
     * Returns country code and national number if successful, null otherwise
     */
    private tryParseInternationalNumber(value: string): {country: string; nationalNumber: string} | null {
        try {
            const parsed = parsePhoneNumber(value);
            if (parsed && parsed.country && parsed.nationalNumber) {
                // Check if we have this country in our list
                const countryExists = this._allPhones.some((p) => p.code === parsed.country);
                if (countryExists) {
                    return {
                        country: parsed.country,
                        nationalNumber: parsed.nationalNumber,
                    };
                }
            }
        } catch {
            // Parsing failed - not a valid international number yet
        }
        return null;
    }

    // Filter countries by phone code when user types +XX
    searchFilterByCode(code: string): void {
        if (!code) {
            this._filteredPhones = this._allPhones;
            return;
        }

        this._filteredPhones = this._allPhones.filter((phone) => phone.phone_code.startsWith(code));
    }

    setPhoneByCode(countryCode: string): void {
        const country = this._allPhones.find((p) => p.code === countryCode.toUpperCase());

        if (country) {
            this.activeCountry = country.code;

            // Remove country code and + from value (exactly like old code)
            this.value = this.value.replace(country.phone_code, "").replace("+", "");

            this.phoneEvents.emit("countryChange", country.code);
        }
    }

    toggleDropdown(): void {
        this.dropdownOpen = !this.dropdownOpen;
    }

    closeDropdown(): void {
        this.dropdownOpen = false;
        this.search = "";
        this._filteredPhones = this._allPhones;
    }

    // Override onBlur from BaseField
    onBlur(): void {
        super.onBlur(); // Call base implementation (emits 'blur' event)
        const result = this.validate();
        this.phoneEvents.emit("valid", result.isValid);
    }

    // isValid getter inherited from BaseField

    applyInitialPhone(value: string): void {
        const normalized = value.trim();
        if (!normalized) return;

        try {
            const parsed = parsePhoneNumber(normalized);
            if (parsed?.country && parsed.nationalNumber) {
                this.activeCountry = parsed.country;
                this.value = parsed.nationalNumber;
                return;
            }
        } catch {
            // Ignore parse errors and fall back to a digits-based normalization below.
        }

        const digits = normalized.replace(/\D/g, "");
        if (!digits) return;

        this.activeCountry = "US";
        this.value = digits.replace(/^1(?=\d{10}$)/, "");
    }

    clear(): void {
        this.value = "";
        this._errors = [];
        this.phoneEvents.emit("change", "", this.activeCountry);
    }

    reset(): void {
        this.value = "";
        this.activeCountry = "US"; // Reset to USA
        this.search = "";
        this._errors = [];
        this._filteredPhones = this._allPhones;
        this.phoneEvents.emit("change", "", this.activeCountry);
    }

    scrollIntoView(): void {
        this.phoneEvents.emit("scrollIntoView");
    }

    // Actions
    async init(): Promise<void> {
        this._isLoading = true;
        this.phoneEvents.emit("loading", true);

        try {
            const phoneDataModule = await import("@/shared/models/form/phone/phone-data");
            const phoneCountries: Array<PhoneCountryData> = phoneDataModule.default;

            // Map to simplified PhoneData interface
            this._allPhones = phoneCountries.map((country) => ({
                code: country.alpha2,
                name: country.country_name,
                phone_code: country.country_code,
            }));

            this._filteredPhones = this._allPhones;
        } catch (err) {
            console.error("Failed to load phone data:", err);
        } finally {
            this._isLoading = false;
            this.phoneEvents.emit("loading", false);
        }
    }
}

// Export type
export type PhoneFieldInstance = PhoneFieldClass;

// Factory function (SSR compatible)
export function PhoneField(config?: PhoneFieldConfig): PhoneFieldInstance {
    return proxy<PhoneFieldInstance>(new PhoneFieldClass(config));
}
